import { format, formatRelative, isToday, isYesterday, isValid, Locale } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import i18next from "i18next";

/**
 * Gets the appropriate date-fns locale based on the current i18n language
 */
export const getDateLocale = (): Locale => {
  // On teste le préfixe : `fr-FR` et `fr-CA` retombaient en anglais parce que
  // le `switch` n'acceptait que le code exact "fr".
  const language = (i18next.language || "").toLowerCase();
  if (language.startsWith("fr")) return fr;
  return enUS;
};

/**
 * Convertit une entrée hétérogène en `Date` exploitable.
 *
 * Pourquoi : date-fns v3 LÈVE `RangeError: Invalid time value` sur une date
 * invalide. Ces helpers sont appelés en plein rendu React un peu partout dans
 * l'application : une seule ligne de données incomplète (chaîne vide, date
 * malformée, `undefined`) suffisait à blanchir l'écran. On renvoie donc `null`
 * et les helpers retombent sur une chaîne de repli.
 */
const toValidDate = (date: Date | string | number | null | undefined): Date | null => {
  if (date === null || date === undefined || date === "") return null;
  const dateObj = typeof date === "string" || typeof date === "number" ? new Date(date) : date;
  return isValid(dateObj) ? dateObj : null;
};

/**
 * Format a date with the current locale
 * @param date The date to format
 * @param formatStr The format string (date-fns format)
 * @param options Optional configuration, can include a specific locale
 * @returns The formatted date string
 */
export const formatDate = (
  date: Date | string | number,
  formatStr: string = "PP",
  options?: { locale?: Locale; fallback?: string }
): string => {
  const dateObj = toValidDate(date);
  if (!dateObj) return options?.fallback ?? "";
  const locale = options?.locale || getDateLocale();
  return format(dateObj, formatStr, { locale });
};

/**
 * Format a time with the current locale
 * @param date The date/time to format
 * @param formatStr The format string (date-fns format)
 * @returns The formatted time string
 */
export const formatTime = (date: Date | string | number, formatStr: string = "p"): string => {
  const dateObj = toValidDate(date);
  if (!dateObj) return "";
  return format(dateObj, formatStr, { locale: getDateLocale() });
};

/**
 * Format a date and time together with the current locale
 * @param date The date/time to format
 * @param formatStr The format string (date-fns format)
 * @returns The formatted date and time string
 */
export const formatDateTime = (date: Date | string | number, formatStr: string = "PPp"): string => {
  const dateObj = toValidDate(date);
  if (!dateObj) return "";
  return format(dateObj, formatStr, { locale: getDateLocale() });
};

/**
 * Format a date relative to now (yesterday, today, tomorrow, next week)
 * @param date The date to format
 * @returns The relative date string
 */
export const formatRelativeDate = (date: Date | string | number): string => {
  const dateObj = toValidDate(date);
  if (!dateObj) return "";

  if (isToday(dateObj)) {
    return i18next.t('common.today');
  }
  
  if (isYesterday(dateObj)) {
    return i18next.t('common.yesterday');
  }
  
  return formatRelative(dateObj, new Date(), { locale: getDateLocale() });
};

/**
 * Format date for database storage
 * @param date 
 * @returns formatted date string YYYY-MM-DD
 */
export const formatDateForDatabase = (date: Date): string => {
  const dateObj = toValidDate(date);
  // Ici on ne peut pas inventer une date : mieux vaut une erreur explicite
  // qu'écrire une valeur fausse en base.
  if (!dateObj) throw new Error('formatDateForDatabase: date invalide');
  return format(dateObj, 'yyyy-MM-dd');
};
